import {
  BaseBadge,
  BaseButton,
  BaseCard,
  BaseInput,
  BaseModal,
  BasePagination,
  BaseSelect,
  BaseTable,
} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useCommissions, useCreateCommission, useDeleteCommission, useUpdateCommission} from "@/hooks/useCommissions";
import type {CommissionConfig} from "@/services/commissions.service";
import {Gift, Pause, Pencil, Play, Trash2} from "lucide-react";
import {useState} from "react";

const operationTypeLabel: Record<string, string> = {
  currency_conversion: "Conversión",
  p2p_transfer: "Transferencia P2P",
  pay_in: "Depósito",
  pay_out: "Retiro",
  remittance: "Remesa",
  withdrawal: "Retiro",
};

const operationTypeOptions = [
  {value: "currency_conversion", label: "Conversión de moneda"},
  {value: "p2p_transfer", label: "Transferencia P2P"},
  {value: "pay_in", label: "Depósito (pay_in)"},
  {value: "pay_out", label: "Retiro (pay_out)"},
  {value: "remittance", label: "Remesa"},
];

const formatCents = (cents: number): string => (cents / 100).toLocaleString("es-CL", {minimumFractionDigits: 2});

export function Commissions() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<CommissionConfig | null>(null);

  const offset = (page - 1) * limit;
  const {data, isLoading} = useCommissions({limit, offset});
  const deleteCommission = useDeleteCommission();
  const updateCommission = useUpdateCommission();

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const columns: Column<CommissionConfig>[] = [
    {
      key: "operation_type",
      header: "Tipo Operación",
      render: row => (
        <span className="text-sm font-medium text-(--color-text-primary)">
          {operationTypeLabel[row.operation_type] ?? row.operation_type}
        </span>
      ),
    },
    {
      key: "currency",
      header: "Moneda",
      render: row => (
        <span className="text-sm text-(--color-text-secondary)">{row.currency === "*" ? "Todas" : row.currency}</span>
      ),
    },
    {
      key: "percentage",
      header: "Porcentaje",
      render: row => (
        <span className="text-sm font-mono tabular-nums text-(--color-text-primary)">{row.percentage}%</span>
      ),
    },
    {
      key: "fixed_cents",
      header: "Fijo",
      render: row => (
        <span className="text-sm font-mono text-(--color-text-secondary)">{formatCents(row.fixed_cents)}</span>
      ),
    },
    {
      key: "promo_active",
      header: "Promo",
      render: row =>
        row.promo_active ? (
          <BaseBadge
            variant="info"
            label={row.promo_label || "Activa"}
          />
        ) : (
          <span className="text-xs text-(--color-text-tertiary)">—</span>
        ),
    },
    {
      key: "is_active",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={row.is_active ? "success" : "neutral"}
          label={row.is_active ? "Activo" : "Inactivo"}
          dot
        />
      ),
    },
    {
      key: "actions",
      header: "Acciones",
      align: "right",
      render: row => (
        <div className="flex items-center gap-1 justify-end">
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => setEditing(row)}
            title="Editar"
          >
            <Pencil size={14} />
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() =>
              updateCommission.mutate({
                id: row.id,
                body: {promo_active: !row.promo_active, promo_label: row.promo_active ? "" : row.promo_label},
              })
            }
            title={row.promo_active ? "Desactivar promo" : "Activar promo"}
          >
            <Gift size={14} />
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => updateCommission.mutate({id: row.id, body: {is_active: !row.is_active}})}
            title={row.is_active ? "Desactivar" : "Activar"}
          >
            {row.is_active ? <Pause size={14} /> : <Play size={14} />}
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => deleteCommission.mutate(row.id)}
            title="Eliminar"
          >
            <Trash2 size={14} />
          </BaseButton>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">Comisiones</h1>
          <p className="text-sm text-(--color-text-secondary) mt-1">{pagination.total} comisiones configuradas</p>
        </div>
        <BaseButton
          variant="primary"
          size="md"
          onClick={() => setShowCreate(true)}
        >
          + Nueva comisión
        </BaseButton>
      </div>

      <BaseCard padding="none">
        <BaseTable<CommissionConfig>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No hay comisiones configuradas."
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>

      {showCreate && <CreateCommissionModal onClose={() => setShowCreate(false)} />}
      {editing && (
        <EditCommissionModal
          commission={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CreateCommissionModal({onClose}: {onClose: () => void}) {
  const create = useCreateCommission();
  const [form, setForm] = useState({
    operation_type: "currency_conversion",
    currency: "*",
    percentage: "",
    fixed_cents: "0",
    min_amount_cents: "0",
    max_amount_cents: "0",
  });
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");
    if (!form.percentage) {
      setError("El porcentaje es requerido");
      return;
    }
    try {
      await create.mutateAsync({
        operation_type: form.operation_type,
        currency: form.currency.toUpperCase() || "*",
        percentage: form.percentage,
        fixed_cents: Number(form.fixed_cents),
        min_amount_cents: Number(form.min_amount_cents),
        max_amount_cents: Number(form.max_amount_cents),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear");
    }
  };

  const set = (key: string, value: string) => setForm(prev => ({...prev, [key]: value}));

  return (
    <BaseModal
      open
      onClose={onClose}
      title="Nueva Comisión"
      size="md"
      footer={
        <>
          <BaseButton
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            loading={create.isPending}
            onClick={handleSubmit}
          >
            Crear
          </BaseButton>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <BaseSelect
          label="Tipo de operación"
          options={operationTypeOptions}
          value={form.operation_type}
          onChange={e => set("operation_type", e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Moneda (* = todas)"
            placeholder="*"
            value={form.currency}
            onChange={e => set("currency", e.target.value)}
          />
          <BaseInput
            label="Porcentaje"
            placeholder="1.0000"
            value={form.percentage}
            onChange={e => set("percentage", e.target.value)}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <BaseInput
            label="Fijo (centavos)"
            type="number"
            value={form.fixed_cents}
            onChange={e => set("fixed_cents", e.target.value)}
          />
          <BaseInput
            label="Mínimo (centavos)"
            type="number"
            value={form.min_amount_cents}
            onChange={e => set("min_amount_cents", e.target.value)}
          />
          <BaseInput
            label="Máximo (centavos)"
            type="number"
            value={form.max_amount_cents}
            onChange={e => set("max_amount_cents", e.target.value)}
          />
        </div>
        {error && <p className="text-sm text-(--color-danger)">{error}</p>}
      </form>
    </BaseModal>
  );
}

function EditCommissionModal({commission, onClose}: {commission: CommissionConfig; onClose: () => void}) {
  const update = useUpdateCommission();
  const [form, setForm] = useState({
    percentage: commission.percentage,
    fixed_cents: String(commission.fixed_cents),
    min_amount_cents: String(commission.min_amount_cents),
    max_amount_cents: String(commission.max_amount_cents),
    promo_label: commission.promo_label,
    promo_active: commission.promo_active,
  });
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");
    try {
      await update.mutateAsync({
        id: commission.id,
        body: {
          percentage: form.percentage,
          fixed_cents: Number(form.fixed_cents),
          min_amount_cents: Number(form.min_amount_cents),
          max_amount_cents: Number(form.max_amount_cents),
          promo_label: form.promo_label,
          promo_active: form.promo_active,
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar");
    }
  };

  const set = (key: string, value: string | boolean) => setForm(prev => ({...prev, [key]: value}));

  return (
    <BaseModal
      open
      onClose={onClose}
      title={`Editar — ${operationTypeLabel[commission.operation_type] ?? commission.operation_type}`}
      size="md"
      footer={
        <>
          <BaseButton
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            loading={update.isPending}
            onClick={handleSubmit}
          >
            Guardar
          </BaseButton>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-(--color-surface-2) text-xs text-(--color-text-secondary)">
          <div>
            <span className="font-medium">Operación:</span>{" "}
            {operationTypeLabel[commission.operation_type] ?? commission.operation_type}
          </div>
          <div>
            <span className="font-medium">Moneda:</span> {commission.currency === "*" ? "Todas" : commission.currency}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Porcentaje"
            value={form.percentage}
            onChange={e => set("percentage", e.target.value)}
          />
          <BaseInput
            label="Fijo (centavos)"
            type="number"
            value={form.fixed_cents}
            onChange={e => set("fixed_cents", e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Mínimo (centavos)"
            type="number"
            value={form.min_amount_cents}
            onChange={e => set("min_amount_cents", e.target.value)}
          />
          <BaseInput
            label="Máximo (centavos)"
            type="number"
            value={form.max_amount_cents}
            onChange={e => set("max_amount_cents", e.target.value)}
          />
        </div>
        <BaseInput
          label="Texto promocional"
          placeholder="¡Gratis este mes!"
          value={form.promo_label}
          onChange={e => set("promo_label", e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
          <input
            type="checkbox"
            checked={form.promo_active}
            onChange={e => set("promo_active", e.target.checked)}
            className="rounded"
          />
          Promo activa (comisión = 0)
        </label>
        {error && <p className="text-sm text-(--color-danger)">{error}</p>}
      </form>
    </BaseModal>
  );
}
